import React from "react";
import ScoreMicroCard, { RenderableData } from "./ScoreMicroCard";
import { HealthReadableResult } from "asset-analysis/lib/@types/outcoming/analysis-readable-result";
import { MdOutlineAttachMoney } from "react-icons/md";
import { GrGrow } from "react-icons/gr";
import { GiHealthNormal, GiReceiveMoney } from "react-icons/gi";
import { LiaCashRegisterSolid } from "react-icons/lia";
import { FaRankingStar } from "react-icons/fa6";
import { metrics } from "./data";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.min.css";
import "swiper/swiper.min.css";
import SwiperCore, { Navigation, Pagination, Scrollbar, A11y } from "swiper";
import Skeleton from "react-loading-skeleton";
import useWindowDimensions from "../../../../hooks/useWindowDimension";
import { isMobile } from "react-device-detect";
SwiperCore.use([Navigation, Pagination, Scrollbar, A11y]);

interface Props {
  analysedReadableData: HealthReadableResult | "empty" | "error" | null;
  resultRawData: any | null;
  assetType: number | null;
}
function StockHealthCard({
  analysedReadableData,
  resultRawData,
  assetType,
}: Props) {
  const { width } = useWindowDimensions();
  if (analysedReadableData === null) {
    return loadingState();
  }

  let userLocation = window.location.pathname;
  let explodeUserLocation = userLocation.split("/");
  if (
    (explodeUserLocation[3] === "Health" &&
      resultRawData &&
      !resultRawData.requestsResults.Health) ||
    (analysedReadableData === "empty" && explodeUserLocation[3] === "Health") ||
    (analysedReadableData === "error" && explodeUserLocation[3] === "Health")
  ) {
    return (
      <div className="text-center mx-auto mg-t-20 pd-10">
        {<GiHealthNormal size={52} />}
        <h4>Saúde do ativo indisponível</h4>
        <span className="tx-14 text-center">
          {assetType === 1
            ? "Infelizmente a saúde do ativo não está disponível para este ativo no momento."
            : "A saúde do ativo só está disponível para ativos do tipo 'ações'."}
        </span>
      </div>
    );
  }

  if (
    analysedReadableData === "empty" ||
    analysedReadableData === "error" ||
    !resultRawData
  ) {
    return <></>;
  }

  const isSucessResponse = resultRawData.requestsResults.Health === 7;

  if (isSucessResponse) {
    const healthData = resultRawData.analysisResults.Health;
    const scores = getScoresData(
      healthData.CashFlow,
      healthData.Growth,
      healthData.Momentum,
      healthData.Profit,
      healthData.RelativeValue
    );

    return (
      <div
        className="card card-dashboard-thirteen animated fadeInDown"
        style={{ paddingRight: 14, paddingLeft: 14 }}
        id={!isMobile ? "analysis-step-3" : ""}
      >
        <h4>Saúde da empresa</h4>
        <p className="az-content-text">
          Confira um resumo direto da saúde da empresa. Lucy, nossa IA, consegue
          avaliar e dar uma nota de 0 a 10 para cada categoria financeira da
          empresa.
        </p>

        {width <= 991 ? (
          <Swiper
            spaceBetween={10}
            className="swiper-slide"
            slidesPerView={2}
            pagination={{ type: "bullets" }}
            id={isMobile ? "analysis-step-3" : ""}
          >
            {scores.map((score, i) => {
              return (
                <SwiperSlide className="mg-b-40" key={i}>
                  <ScoreMicroCard
                    key={i}
                    name={score.name}
                    historic={score.historic}
                    score={score.score}
                    icon={score.icon}
                    usedMetrics={score.usedMetrics}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        ) : (
          <div
            className={`row mg-t-20 mg-b-20 ${
              width > 991 && "justify-content-center"
            }`}
          >
            {scores.map((score, i) => {
              return (
                <div
                  className="col-6 col-sm-4 col-md-3 col-lg-2 mg-b-10"
                  key={i}
                >
                  <ScoreMicroCard
                    key={i}
                    name={score.name}
                    historic={score.historic}
                    score={score.score}
                    icon={score.icon}
                    usedMetrics={score.usedMetrics}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  } else {
    return <></>;
  }
}

export const StockHealthCardMemo = React.memo(StockHealthCard);

interface History {
  date: string;
  timestamp: number;
  value: number;
}

export interface Score {
  Actual: number;
  Min: number;
  Max: number;
  Grade: string;
  Historic: History[];
}

function getScoresData(
  CashFlow: Score,
  Growth: Score,
  Momentum: Score,
  Profit: Score,
  RelativeValue: Score
): RenderableData[] {
  const iconSize = 32;
  return [
    {
      name: "Crescimento",
      score: Growth.Actual,
      icon: <GrGrow size={iconSize} />,
      usedMetrics: metrics.growthMetrics,
      historic: Growth.Historic,
    },
    {
      name: "Lucro",
      score: Profit.Actual,
      icon: <MdOutlineAttachMoney size={iconSize} />,
      usedMetrics: metrics.profit,
      historic: Profit.Historic,
    },
    {
      name: "Fluxo de caixa",
      score: CashFlow.Actual,
      icon: <LiaCashRegisterSolid size={iconSize} />,
      usedMetrics: metrics.cashflow,
      historic: CashFlow.Historic,
    },
    {
      name: "Retornos",
      score: Momentum.Actual,
      icon: <GiReceiveMoney size={iconSize} />,
      usedMetrics: metrics.returns,
      historic: Momentum.Historic,
    },
    {
      name: "Concorrência",
      score: RelativeValue.Actual,
      icon: <FaRankingStar size={iconSize} />,
      usedMetrics: metrics.relativeValue,
      historic: RelativeValue.Historic,
    },
  ];
}

function loadingState() {
  return (
    <div
      className="card card-dashboard-thirteen animated fadeInDown"
      style={{ paddingRight: 15, paddingLeft: 15 }}
    >
      <h4>Saúde da empresa</h4>
      <p className="az-content-text">
        Confira de maneira simplificada um resumo da saúde da empresa.
      </p>

      <div className="text-center mg-t-20 mg-b-20">
        <Skeleton count={6} width={"70%"}></Skeleton>
      </div>
    </div>
  );
}
